import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { HIDDEN_COMPANY_TYPES } from "@constants/partnerTypes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showNotification } from "context/notification.service";
import { planningTemplatesApi } from "@services/api";
import { PlanningModel } from "types/planning";
import {
  PlanningTemplate,
  PlanningTemplateSection,
  PlanningTemplateStep,
} from "types/planning_template";
import { MODEL_WEEK_COUNTS } from "../../utils/planning_constants";
import {
  cellColor,
  headerBg,
  headerText,
  FALLBACK_CELL_COLOR,
  FALLBACK_HEADER_BG,
  FALLBACK_HEADER_TEXT,
} from "../../utils/planning_structure";
import { REFERENCE_START } from "@utils/planning_utils";

/** Generate a URL-safe stable ID from a label. */
function slugify(label: string): string {
  return (
    label
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || `s_${Math.random().toString(36).slice(2, 8)}`
  );
}

/** Make an ID unique within a set by appending a numeric suffix if needed. */
function uniqueId(base: string, existing: Set<string>): string {
  if (!existing.has(base)) return base;
  let i = 2;
  while (existing.has(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

// ─── Week column helpers (same as PlanningTable) ─────────────────────────────

const FRENCH_MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

interface WeekColumn {
  index: number;
  isoWeek: number;
  month: number;
  monthLabel: string;
  year: number;
  isBlocked: boolean;
}

function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((d.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7,
    )
  );
}

const MODELS_WITHOUT_CONGES = new Set(["12_mois_ordre"]);

function computeWeekColumns(startDate: Date, model: string): WeekColumn[] {
  const n = MODEL_WEEK_COUNTS[model] ?? 48;
  const hasConges = !MODELS_WITHOUT_CONGES.has(model);
  return Array.from({ length: n }, (_, i) => {
    const monday = new Date(startDate);
    monday.setDate(monday.getDate() + i * 7);
    const isoWeek = getISOWeek(monday);
    const calMonth = monday.getMonth() + 1;
    let month = calMonth;
    let year = monday.getFullYear();
    if (isoWeek === 1 && calMonth === 12) {
      month = 1;
      year += 1;
    } else if (isoWeek >= 52 && calMonth === 1) {
      month = 12;
      year -= 1;
    }
    return {
      index: i,
      isoWeek,
      month,
      monthLabel: FRENCH_MONTHS[month - 1],
      year,
      isBlocked:
        hasConges &&
        ((isoWeek >= 31 && isoWeek <= 35) ||
          (calMonth === 12 && monday.getDate() >= 20)),
    };
  });
}

interface MonthGroup {
  label: string;
  year: number;
  colSpan: number;
}

function buildMonthGroups(cols: WeekColumn[]): MonthGroup[] {
  const groups: MonthGroup[] = [];
  for (const col of cols) {
    const last = groups[groups.length - 1];
    if (last && last.label === col.monthLabel && last.year === col.year) {
      last.colSpan++;
    } else {
      groups.push({ label: col.monthLabel, year: col.year, colSpan: 1 });
    }
  }
  return groups;
}

// ─── Model options ───────────────────────────────────────────────────────────

const modelOptions = [
  { value: "10_mois_avec_conges", label: "10 mois (44 sem.)" },
  { value: "11_mois", label: "11 mois (48 sem.)" },
  { value: "12_mois", label: "12 mois (52 sem.)" },
  { value: "12_mois_ordre", label: "12 mois ordre (52 sem.)" },
  { value: "13_mois_avec_conges", label: "13 mois (56 sem.)" },
  { value: "14_mois_avec_conges", label: "14 mois (60 sem.)" },
  { value: "15_mois_avec_conges", label: "15 mois (64 sem.)" },
  { value: "16_mois_avec_conges", label: "16 mois (68 sem.)" },
  { value: "17_mois_avec_conges", label: "17 mois (72 sem.)" },
  { value: "18_mois_avec_conges", label: "18 mois (76 sem.)" },
];

/** Deep clone a section array so local edits don't mutate the store's copy. */
function cloneSections(sections: PlanningTemplateSection[]): PlanningTemplateSection[] {
  return sections.map((s) => ({
    ...s,
    steps: (s.steps || []).map((st) => ({ ...st, default_weeks: [...st.default_weeks] })),
  }));
}

/** Return visible (non-deleted) sections sorted by order, with deleted ones untouched. */
function visibleSortedSections(sections: PlanningTemplateSection[]): PlanningTemplateSection[] {
  return sections
    .filter((s) => !s.deleted_at && !HIDDEN_COMPANY_TYPES.has(s.section_key))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

// ─── Component ───────────────────────────────────────────────────────────────

const COL_W = 28;
const LABEL_W = 260;

export const PlanningTemplateModal = ({
  onClose,
  templateId,
}: {
  onClose: () => void;
  templateId: string;
}) => {
  const planningTemplates = useProjectStore(
    (state) => state.planningTemplates,
  );
  const userData = useDataStore((state) => state.userData);

  const existingTemplate = planningTemplates.find((t) => t.id === templateId);
  const isNew = !existingTemplate;

  const [name, setName] = useState(existingTemplate?.name || "");
  const [model, setModel] = useState<PlanningModel>(
    existingTemplate?.model || "12_mois",
  );
  const [sections, setSections] = useState<PlanningTemplateSection[]>(
    existingTemplate ? cloneSections(existingTemplate.sections) : [],
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // The sections visible in the grid (non-deleted, ordered).
  const renderedSections = useMemo(() => visibleSortedSections(sections), [sections]);

  // --- Section CRUD handlers ---

  const updateSection = useCallback(
    (sectionKey: string, patch: Partial<PlanningTemplateSection>) => {
      setSections((prev) =>
        prev.map((s) => (s.section_key === sectionKey ? { ...s, ...patch } : s)),
      );
    },
    [],
  );

  const addSection = useCallback(() => {
    setSections((prev) => {
      const existingKeys = new Set(prev.map((s) => s.section_key));
      const newKey = uniqueId(slugify("nouvelle_section"), existingKeys);
      const maxOrder = prev.reduce((max, s) => Math.max(max, s.order ?? 0), -1);
      const newSection: PlanningTemplateSection = {
        section_key: newKey,
        label: "Nouvelle section",
        order: maxOrder + 1,
        cell_color: FALLBACK_CELL_COLOR,
        header_bg: FALLBACK_HEADER_BG,
        header_text: FALLBACK_HEADER_TEXT,
        deleted_at: null,
        steps: [],
      };
      return [...prev, newSection];
    });
  }, []);

  const softDeleteSection = useCallback((sectionKey: string) => {
    updateSection(sectionKey, { deleted_at: new Date().toISOString() });
  }, [updateSection]);

  const restoreSection = useCallback((sectionKey: string) => {
    updateSection(sectionKey, { deleted_at: null });
  }, [updateSection]);

  const moveSection = useCallback((sectionKey: string, direction: "up" | "down") => {
    setSections((prev) => {
      const visible = visibleSortedSections(prev);
      const idx = visible.findIndex((s) => s.section_key === sectionKey);
      if (idx < 0) return prev;
      const swap = direction === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= visible.length) return prev;
      const a = visible[idx];
      const b = visible[swap];
      return prev.map((s) => {
        if (s.section_key === a.section_key) return { ...s, order: b.order ?? 0 };
        if (s.section_key === b.section_key) return { ...s, order: a.order ?? 0 };
        return s;
      });
    });
  }, []);

  // --- Step CRUD handlers ---

  const updateStep = useCallback(
    (sectionKey: string, stepKey: string, patch: Partial<PlanningTemplateStep>) => {
      setSections((prev) =>
        prev.map((s) => {
          if (s.section_key !== sectionKey) return s;
          return {
            ...s,
            steps: s.steps.map((st) => (st.step_key === stepKey ? { ...st, ...patch } : st)),
          };
        }),
      );
    },
    [],
  );

  const addStep = useCallback((sectionKey: string) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.section_key !== sectionKey) return s;
        const existingKeys = new Set(s.steps.map((st) => st.step_key));
        const newKey = uniqueId(slugify(`${s.section_key}_etape`), existingKeys);
        const maxOrder = s.steps.reduce((max, st) => Math.max(max, st.order ?? 0), -1);
        const newStep: PlanningTemplateStep = {
          step_key: newKey,
          label: "Nouvelle étape",
          order: maxOrder + 1,
          deleted_at: null,
          default_weeks: [],
        };
        return { ...s, steps: [...s.steps, newStep] };
      }),
    );
  }, []);

  const softDeleteStep = useCallback((sectionKey: string, stepKey: string) => {
    updateStep(sectionKey, stepKey, { deleted_at: new Date().toISOString() });
  }, [updateStep]);

  const restoreStep = useCallback((sectionKey: string, stepKey: string) => {
    updateStep(sectionKey, stepKey, { deleted_at: null });
  }, [updateStep]);

  const moveStep = useCallback(
    (sectionKey: string, stepKey: string, direction: "up" | "down") => {
      setSections((prev) =>
        prev.map((s) => {
          if (s.section_key !== sectionKey) return s;
          const visible = s.steps
            .filter((st) => !st.deleted_at)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          const idx = visible.findIndex((st) => st.step_key === stepKey);
          if (idx < 0) return s;
          const swap = direction === "up" ? idx - 1 : idx + 1;
          if (swap < 0 || swap >= visible.length) return s;
          const a = visible[idx];
          const b = visible[swap];
          return {
            ...s,
            steps: s.steps.map((st) => {
              if (st.step_key === a.step_key) return { ...st, order: b.order ?? 0 };
              if (st.step_key === b.step_key) return { ...st, order: a.order ?? 0 };
              return st;
            }),
          };
        }),
      );
    },
    [],
  );

  // Drag-to-paint state
  const isDragging = useRef(false);
  const dragAction = useRef<"add" | "remove">("add");

  // Scroll-drag state (grab and drag to scroll horizontally)
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrollDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const [scrollCursor, setScrollCursor] = useState(false);

  useEffect(() => {
    const onWindowMouseUp = () => {
      isScrollDragging.current = false;
      isDragging.current = false;
      setScrollCursor(false);
    };
    window.addEventListener("mouseup", onWindowMouseUp);
    return () => window.removeEventListener("mouseup", onWindowMouseUp);
  }, []);

  const onScrollMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Only start scroll-drag from container or non-cell elements
    e.preventDefault();
    isScrollDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
  }, []);

  const onScrollMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isScrollDragging.current) return;
    const dx = e.clientX - dragStartX.current;
    if (Math.abs(dx) > 5) {
      hasDragged.current = true;
      isDragging.current = false; // cancel any cell painting
      setScrollCursor(true);
      if (scrollRef.current)
        scrollRef.current.scrollLeft = dragScrollLeft.current - dx;
    }
  }, []);

  const onScrollMouseUp = useCallback(() => {
    isScrollDragging.current = false;
    setScrollCursor(false);
  }, []);

  // Compute week columns from model
  const cols = useMemo(
    () => computeWeekColumns(REFERENCE_START, model),
    [model],
  );
  const monthGroups = useMemo(() => buildMonthGroups(cols), [cols]);

  const toggleWeek = (
    sectionKey: string,
    stepKey: string,
    week: number,
  ) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.section_key !== sectionKey) return s;
        return {
          ...s,
          steps: s.steps.map((st) => {
            if (st.step_key !== stepKey) return st;
            const has = st.default_weeks.includes(week);
            return {
              ...st,
              default_weeks: has
                ? st.default_weeks.filter((w) => w !== week)
                : [...st.default_weeks, week].sort((a, b) => a - b),
            };
          }),
        };
      }),
    );
  };

  const setWeek = (
    sectionKey: string,
    stepKey: string,
    week: number,
    action: "add" | "remove",
  ) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.section_key !== sectionKey) return s;
        return {
          ...s,
          steps: s.steps.map((st) => {
            if (st.step_key !== stepKey) return st;
            if (action === "add" && !st.default_weeks.includes(week)) {
              return {
                ...st,
                default_weeks: [...st.default_weeks, week].sort(
                  (a, b) => a - b,
                ),
              };
            }
            if (action === "remove" && st.default_weeks.includes(week)) {
              return {
                ...st,
                default_weeks: st.default_weeks.filter((w) => w !== week),
              };
            }
            return st;
          }),
        };
      }),
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showNotification({
        type: "error",
        message: "Le nom du modèle est obligatoire",
      });
      return;
    }

    if (isNew) {
      try {
        const result = await planningTemplatesApi.create({
          name,
          org_id: userData?.org_id || "",
          model,
          sections,
        });
        useProjectStore.getState().addPlanningTemplate(result);
        showNotification({
          type: "success",
          message: "Modèle créé avec succès",
        });
        onClose();
      } catch (error) {
        console.error("Error creating planning template:", error);
        showNotification({
          type: "error",
          message: "Erreur lors de la création du modèle",
        });
      }
    } else {
      const updated: PlanningTemplate = {
        ...existingTemplate,
        name,
        model,
        sections,
      };
      useProjectStore.getState().updatePlanningTemplate(updated);
      showNotification({ type: "success", message: "Modèle enregistré" });
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!existingTemplate) return;
    try {
      useProjectStore
        .getState()
        .removePlanningTemplates([existingTemplate.id]);
      showNotification({ type: "success", message: "Modèle supprimé" });
      onClose();
    } catch (error) {
      console.error("Error deleting planning template:", error);
    }
  };

  const handleDuplicate = async () => {
    if (!existingTemplate) return;
    try {
      const result = await planningTemplatesApi.create({
        name: `Copie de ${name}`,
        org_id: userData?.org_id || "",
        model,
        sections: structuredClone(sections),
      });
      useProjectStore.getState().addPlanningTemplate(result);
      showNotification({
        type: "success",
        message: "Modèle dupliqué avec succès",
      });
      onClose();
    } catch (error) {
      console.error("Error duplicating planning template:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la duplication du modèle",
      });
    }
  };

  return (
    <>
      <BaseModal
        title={
          isNew
            ? "Nouveau modèle de planning"
            : "Modifier le modèle de planning"
        }
        startIcon={<Icon1 name="planning" size={20} />}
        onClose={onClose}
        width="max-w-7xl"
      >
        <div className="bg-white flex flex-col gap-4 p-4">
          <div className="flex flex-row gap-4">
            <div className="flex-1">
              <TextField
                label="Nom du modèle"
                placeholder="Construction maison individuelle"
                value={name}
                onChange={(value) => setName(value)}
              />
            </div>
            <div className="w-250">
              <DropDown
                label="Durée"
                options={modelOptions}
                value={model}
                onSelect={(val) => setModel(val as PlanningModel)}
                category="Modèle"
              />
            </div>
          </div>

          {/* Help banner */}
          <div className="rounded-lg border border-blue-200 bg-blue-50">
            <button
              onClick={() => setShowHelp((v) => !v)}
              className="w-full flex flex-row items-center gap-2 px-3 py-2 text-h4 text-blue-800 hover:bg-blue-100 rounded-lg"
            >
              <span className="font-medium">
                {showHelp ? "▼ Masquer l'aide" : "▶ Comment ça marche ?"}
              </span>
            </button>
            {showHelp && (
              <div className="px-4 pb-3 pt-1 text-h5 text-neutral-700 flex flex-col gap-2">
                <div>
                  <span className="font-semibold text-neutral-800">Ce modèle s'applique uniquement aux nouveaux plannings.</span>{" "}
                  Toutes les modifications faites ici (sections, étapes, semaines par défaut)
                  n'affectent pas les plannings de projets déjà créés. Chaque planning de projet
                  garde sa propre copie indépendante au moment de sa création.
                </div>
                <div>
                  <span className="font-semibold text-neutral-800">Modifier les sections et étapes.</span>{" "}
                  Cliquez sur le nom d'une section ou d'une étape pour le renommer.
                  Utilisez les flèches ▲▼ pour réordonner. Ajoutez une étape avec le bouton +
                  dans l'en-tête de section, ou une nouvelle section avec le bouton « Ajouter
                  une section » en bas.
                </div>
                <div>
                  <span className="font-semibold text-neutral-800">Suppression.</span>{" "}
                  Les sections et étapes supprimées sont masquées mais conservées (suppression
                  douce) — vous pouvez les restaurer via le bouton « Restaurer ». Les projets
                  existants ne sont jamais affectés.
                </div>
                <div>
                  <span className="font-semibold text-neutral-800">Charge artisan & assignation des intervenants.</span>{" "}
                  La vue « Charge artisan » et l'onglet « Intervenants » d'un projet utilisent
                  les sections de tous les modèles de planning. Si vous ajoutez une section ici,
                  elle apparaîtra automatiquement dans ces écrans pour les nouveaux projets.
                  Les anciens projets gardent leurs anciennes sections.
                </div>
                <div>
                  <span className="font-semibold text-neutral-800">Semaines par défaut.</span>{" "}
                  Les semaines coloriées dans la grille ci-dessous sont uniquement des valeurs
                  par défaut pré-remplies à la création d'un nouveau planning.
                </div>
              </div>
            )}
          </div>

          {/* Week grid — same layout as PlanningTable */}
          <div
            ref={scrollRef}
            className={`overflow-auto max-h-[60vh] rounded-xl border border-neutral-200 ${scrollCursor ? "cursor-grabbing select-none" : "cursor-grab"}`}
            onMouseDown={onScrollMouseDown}
            onMouseMove={onScrollMouseMove}
            onMouseUp={onScrollMouseUp}
            onMouseLeave={() => { isDragging.current = false; isScrollDragging.current = false; setScrollCursor(false); }}
          >
            <table
              className="border-separate border-spacing-0"
              style={{ minWidth: LABEL_W + cols.length * COL_W }}
            >
              <thead className="sticky top-0 z-30">
                {/* Row 1 — months (merged) */}
                <tr>
                  <td
                    className="sticky left-0 z-40 bg-neutral-100 border border-neutral-200 px-2 py-1 text-h4 text-neutral-500"
                    style={{ width: LABEL_W, minWidth: LABEL_W }}
                  >
                    Mois
                  </td>
                  {monthGroups.map((g, i) => (
                    <td
                      key={i}
                      className="relative z-30 bg-neutral-100 border border-neutral-200 text-h4 font-medium text-center"
                      colSpan={g.colSpan}
                      style={{ minWidth: g.colSpan * COL_W }}
                    >
                      {g.label}
                    </td>
                  ))}
                </tr>

                {/* Row 2 — ISO week numbers */}
                <tr>
                  <td
                    className="sticky left-0 z-40 bg-neutral-50 border border-neutral-200 px-2 py-1 text-h4 text-neutral-400"
                    style={{ width: LABEL_W, minWidth: LABEL_W }}
                  >
                    Semaine
                  </td>
                  {cols.map((col) => (
                    <td
                      key={col.index}
                      className={`relative z-30 border border-neutral-200 text-center text-h4 font-medium ${
                        col.isBlocked
                          ? "bg-gray-300 text-gray-400"
                          : "bg-neutral-50 text-neutral-600"
                      }`}
                      style={{ width: COL_W, minWidth: COL_W }}
                    >
                      {col.isoWeek}
                    </td>
                  ))}
                </tr>
              </thead>

              <tbody>
                {renderedSections.map((section, idx) => (
                  <TemplateSectionRows
                    key={section.section_key}
                    section={section}
                    sectionIndex={idx}
                    totalVisible={renderedSections.length}
                    cols={cols}
                    isDragging={isDragging}
                    dragAction={dragAction}
                    hasDragged={hasDragged}
                    onToggleWeek={toggleWeek}
                    onSetWeek={setWeek}
                    onRenameSection={(label) => updateSection(section.section_key, { label })}
                    onDeleteSection={() => softDeleteSection(section.section_key)}
                    onMoveSection={(dir) => moveSection(section.section_key, dir)}
                    onAddStep={() => addStep(section.section_key)}
                    onRenameStep={(stepKey, label) =>
                      updateStep(section.section_key, stepKey, { label })
                    }
                    onDeleteStep={(stepKey) =>
                      softDeleteStep(section.section_key, stepKey)
                    }
                    onMoveStep={(stepKey, dir) =>
                      moveStep(section.section_key, stepKey, dir)
                    }
                    onRestoreStep={(stepKey) =>
                      restoreStep(section.section_key, stepKey)
                    }
                  />
                ))}
                {/* Add section row */}
                <tr>
                  <td
                    colSpan={cols.length + 1}
                    className="sticky left-0 px-2 py-2 bg-neutral-50 border-t border-neutral-200"
                    style={{ width: LABEL_W + cols.length * COL_W }}
                  >
                    <SecondaryButton
                      variant="small"
                      startIcon={<Icon1 name="plus" size={14} />}
                      onClick={addSection}
                    >
                      Ajouter une section
                    </SecondaryButton>
                  </td>
                </tr>
                {/* Show soft-deleted sections at the bottom */}
                {sections
                  .filter((s) => s.deleted_at && !HIDDEN_COMPANY_TYPES.has(s.section_key))
                  .map((s) => (
                  <tr key={`deleted-${s.section_key}`}>
                    <td
                      colSpan={cols.length + 1}
                      className="sticky left-0 px-2 py-2 bg-red-50 border-t border-red-100"
                    >
                      <div className="flex flex-row items-center gap-2">
                        <span className="text-h4 text-neutral-500 italic line-through flex-1">
                          {s.label || s.section_key}
                        </span>
                        <SecondaryButton
                          variant="small"
                          onClick={() => restoreSection(s.section_key)}
                        >
                          Restaurer
                        </SecondaryButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border-t-1 p-4 flex flex-row justify-between">
          <div className="flex flex-row gap-2">
            {!isNew && (
              <SecondaryButton
                variant="small"
                danger={true}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Supprimer
              </SecondaryButton>
            )}
            {!isNew && (
              <SecondaryButton
                variant="small"
                startIcon={<Icon1 name="copy" size={20} />}
                onClick={handleDuplicate}
              >
                Dupliquer
              </SecondaryButton>
            )}
          </div>
          <div className="flex flex-row gap-2">
            <SecondaryButton variant="small" onClick={onClose}>
              Annuler
            </SecondaryButton>
            <Button variant="small" label="Enregistrer" onClick={handleSave} />
          </div>
        </div>
      </BaseModal>

      {showDeleteConfirm && (
        <ConfirmationModal
          title="Supprimer ce modèle de planning ?"
          confirmText="CONFIRMER"
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
};

// ─── Section rows sub-component ──────────────────────────────────────────────

interface TemplateSectionRowsProps {
  section: PlanningTemplateSection;
  sectionIndex: number;
  totalVisible: number;
  cols: WeekColumn[];
  isDragging: React.MutableRefObject<boolean>;
  dragAction: React.MutableRefObject<"add" | "remove">;
  hasDragged: React.MutableRefObject<boolean>;
  onToggleWeek: (sectionKey: string, stepKey: string, week: number) => void;
  onSetWeek: (
    sectionKey: string,
    stepKey: string,
    week: number,
    action: "add" | "remove",
  ) => void;
  onRenameSection: (label: string) => void;
  onDeleteSection: () => void;
  onMoveSection: (direction: "up" | "down") => void;
  onAddStep: () => void;
  onRenameStep: (stepKey: string, label: string) => void;
  onDeleteStep: (stepKey: string) => void;
  onMoveStep: (stepKey: string, direction: "up" | "down") => void;
  onRestoreStep: (stepKey: string) => void;
}

function TemplateSectionRows({
  section,
  sectionIndex,
  totalVisible,
  cols,
  isDragging,
  dragAction,
  hasDragged,
  onToggleWeek,
  onSetWeek,
  onRenameSection,
  onDeleteSection,
  onMoveSection,
  onAddStep,
  onRenameStep,
  onDeleteStep,
  onMoveStep,
  onRestoreStep,
}: TemplateSectionRowsProps) {
  const hbg = headerBg(section);
  const htxt = headerText(section);
  const cellBg = cellColor(section);

  const visibleSteps = useMemo(
    () =>
      (section.steps || [])
        .filter((st) => !st.deleted_at)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [section.steps],
  );
  const deletedSteps = (section.steps || []).filter((st) => st.deleted_at);

  return (
    <>
      {/* Section header row with inline controls */}
      <tr>
        <td
          className="sticky left-0 z-10 px-2 py-1"
          style={{ width: LABEL_W, minWidth: LABEL_W, backgroundColor: hbg, color: htxt }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex flex-row items-center gap-1">
            <div className="flex flex-col">
              <button
                onClick={() => onMoveSection("up")}
                disabled={sectionIndex === 0}
                className="opacity-60 hover:opacity-100 disabled:opacity-20"
                title="Monter"
              >
                <Icon1 name="up2" size={11} />
              </button>
              <button
                onClick={() => onMoveSection("down")}
                disabled={sectionIndex === totalVisible - 1}
                className="opacity-60 hover:opacity-100 disabled:opacity-20"
                title="Descendre"
              >
                <Icon1 name="down2" size={11} />
              </button>
            </div>
            <input
              value={section.label || ""}
              onChange={(e) => onRenameSection(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 bg-transparent text-h3 font-bold outline-none border-b border-transparent focus:border-current"
              style={{ color: htxt, minWidth: 0 }}
              placeholder="Nom de la section"
            />
            <button
              onClick={onAddStep}
              className="opacity-60 hover:opacity-100"
              title="Ajouter une étape"
            >
              <Icon1 name="plus" size={13} />
            </button>
            <button
              onClick={onDeleteSection}
              className="opacity-60 hover:opacity-100"
              title="Supprimer la section"
            >
              <Icon1 name="delete" size={13} />
            </button>
          </div>
        </td>
        {cols.map((col) => (
          <td
            key={col.index}
            className="border-b border-neutral-200"
            style={{
              width: COL_W,
              backgroundColor: col.isBlocked ? "#D1D5DB" : hbg,
              opacity: col.isBlocked ? 1 : 0.3,
            }}
          />
        ))}
      </tr>

      {/* Step rows */}
      {visibleSteps.map((stepDef, stepIdx) => {
        const selectedWeeks = stepDef.default_weeks || [];

        return (
          <tr key={stepDef.step_key}>
            <td
              className="sticky left-0 z-10 px-3 py-1 bg-white border-b border-neutral-100"
              style={{ width: LABEL_W, minWidth: LABEL_W }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex flex-row items-center gap-1">
                <div className="flex flex-col">
                  <button
                    onClick={() => onMoveStep(stepDef.step_key, "up")}
                    disabled={stepIdx === 0}
                    className="text-neutral-300 hover:text-neutral-600 disabled:opacity-20"
                  >
                    <Icon1 name="up2" size={10} />
                  </button>
                  <button
                    onClick={() => onMoveStep(stepDef.step_key, "down")}
                    disabled={stepIdx === visibleSteps.length - 1}
                    className="text-neutral-300 hover:text-neutral-600 disabled:opacity-20"
                  >
                    <Icon1 name="down2" size={10} />
                  </button>
                </div>
                <input
                  value={stepDef.label || ""}
                  onChange={(e) => onRenameStep(stepDef.step_key, e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 bg-transparent text-h4 text-neutral-600 outline-none border-b border-transparent focus:border-neutral-300"
                  style={{ minWidth: 0 }}
                  placeholder="Nom de l'étape"
                />
                <button
                  onClick={() => onDeleteStep(stepDef.step_key)}
                  className="text-neutral-300 hover:text-red-500"
                  title="Supprimer l'étape"
                >
                  <Icon1 name="delete" size={11} />
                </button>
              </div>
            </td>
            {cols.map((col) => {
              const isSelected = selectedWeeks.includes(col.index);
              return (
                <td
                  key={col.index}
                  className={`border-b border-neutral-100 transition-colors ${
                    col.isBlocked
                      ? "cursor-not-allowed"
                      : isSelected
                        ? "cursor-pointer"
                        : "bg-white hover:bg-neutral-50 cursor-pointer"
                  }`}
                  style={{
                    width: COL_W,
                    height: 24,
                    backgroundColor: col.isBlocked
                      ? "#E5E7EB"
                      : isSelected
                        ? cellBg
                        : undefined,
                  }}
                  title={
                    col.isBlocked
                      ? "Congés"
                      : `S${col.isoWeek} — ${col.monthLabel}`
                  }
                  onMouseDown={(e) => {
                    if (col.isBlocked) return;
                    e.stopPropagation();
                    isDragging.current = true;
                    hasDragged.current = false;
                    dragAction.current = isSelected ? "remove" : "add";
                    onToggleWeek(section.section_key, stepDef.step_key, col.index);
                  }}
                  onMouseEnter={() => {
                    if (!isDragging.current || hasDragged.current || col.isBlocked) return;
                    onSetWeek(
                      section.section_key,
                      stepDef.step_key,
                      col.index,
                      dragAction.current,
                    );
                  }}
                  onMouseUp={() => {
                    isDragging.current = false;
                  }}
                />
              );
            })}
          </tr>
        );
      })}

      {/* Deleted steps — compact row with restore button */}
      {deletedSteps.map((step) => (
        <tr key={`del-${step.step_key}`}>
          <td
            colSpan={cols.length + 1}
            className="sticky left-0 px-3 py-1 bg-red-50 border-b border-red-100"
            style={{ width: LABEL_W }}
          >
            <div className="flex flex-row items-center gap-2">
              <span className="text-h4 text-neutral-500 italic line-through flex-1">
                {step.label || step.step_key}
              </span>
              <button
                onClick={() => onRestoreStep(step.step_key)}
                className="text-h5 text-blue-600 hover:text-blue-800"
              >
                Restaurer
              </button>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

